import React from "react";
import axios from "axios";

import { postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_post_public_form_submit,
  API_post_learning_form_completion,
} from "../../../../constants";
import { visibleFields, isAnswered } from "../../create_training_content/formLogic";
import { isDisplayOnlyType } from "../../create_training_content/formFieldHelpers";

/**
 * State + submit logic for the one-question-per-screen FormPlayer. Mirrors
 * useSwipeDeck's mode split:
 *   - "public":  anonymous /f/<token> page. Submits with bare axios (no auth)
 *                to the public endpoint; returns {score, is_passed, ...}.
 *   - "tracked": a learner filling a section-embedded form. Submits a
 *                LearningFormCompletion exactly like Form.js does.
 *   - "preview": editor preview. Never writes anything.
 *
 * Display conditions are re-evaluated on every answer (visibleFields), so a
 * gated follow-up appears as soon as its gate is answered; the server applies
 * the same rules on submit.
 */
export function useFormPlayer({
  mode,
  form,
  shareToken,
  companyId,
  formUuid,
  sectionUuid,
  employeeID,
  initialResponses,
}) {
  const [responses, setResponses] = React.useState(() => initialResponses || {});
  const [respondentName, setRespondentName] = React.useState("");
  const [respondentEmail, setRespondentEmail] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | submitting | done
  const [submitError, setSubmitError] = React.useState(null);
  const [result, setResult] = React.useState(null);

  const fields = React.useMemo(
    () => (form?.fields || []).filter((f) => f && f.id),
    [form]
  );
  const visible = React.useMemo(() => visibleFields(fields, responses), [fields, responses]);

  const setResponse = React.useCallback((fieldId, value) => {
    setResponses((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // Required check for ONE field (the current slide).
  const isFieldSatisfied = React.useCallback(
    (f) => {
      if (!f || isDisplayOnlyType(f.type) || !f.required) return true;
      const v = responses[f.id];
      if (f.type === "checkbox") return !!v;
      if (f.type === "yes_no") return v === true || v === false;
      return isAnswered(v);
    },
    [responses]
  );

  const collectRespondent = mode === "public" && !!form?.collect_respondent_info;
  const respondentSatisfied =
    !collectRespondent || (respondentName.trim() && respondentEmail.trim());

  const submit = React.useCallback(async () => {
    if (status === "submitting" || status === "done") return null;
    setStatus("submitting");
    setSubmitError(null);
    // Only ship answers for fields the respondent actually saw. The server drops
    // hidden answers too; this keeps the payload honest.
    const visibleIds = new Set(visible.map((f) => f.id));
    const payload = {};
    Object.keys(responses).forEach((k) => {
      if (visibleIds.has(k)) payload[k] = responses[k];
    });
    try {
      if (mode === "public") {
        const res = await axios.post(API_post_public_form_submit, {
          share_token: shareToken,
          responses: payload,
          respondent_name: respondentName.trim() || undefined,
          respondent_email: respondentEmail.trim() || undefined,
        });
        const data = res?.data || { ok: true };
        setResult(data);
        setStatus("done");
        return data;
      }
      if (mode === "tracked") {
        const res = await postRightworkAPI(API_post_learning_form_completion, {
          company_id: companyId,
          learning_form_uuid: formUuid,
          section_uuid: sectionUuid,
          responses: JSON.stringify(payload),
          store_team_member_id: employeeID,
        });
        const completion = res?.data?.redux?.payload?.form_completion || {
          responses: payload,
          grading_status: "not_required",
        };
        setResult(completion);
        setStatus("done");
        return completion;
      }
      // preview: fake it
      const fake = { ok: true, preview: true, responses: payload };
      setResult(fake);
      setStatus("done");
      return fake;
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.system?.message ||
        "Couldn't submit. Please try again.";
      setSubmitError(msg);
      setStatus("idle");
      return null;
    }
  }, [
    status,
    visible,
    responses,
    mode,
    shareToken,
    respondentName,
    respondentEmail,
    companyId,
    formUuid,
    sectionUuid,
    employeeID,
  ]);

  const reset = React.useCallback(() => {
    setResponses({});
    setRespondentName("");
    setRespondentEmail("");
    setStatus("idle");
    setSubmitError(null);
    setResult(null);
  }, []);

  return {
    fields,
    visible,
    responses,
    setResponse,
    respondentName,
    setRespondentName,
    respondentEmail,
    setRespondentEmail,
    collectRespondent,
    respondentSatisfied,
    isFieldSatisfied,
    status,
    submitError,
    result,
    submit,
    reset,
  };
}
